import { useEffect, useRef, useCallback } from 'react';
import webSocketService from '../services/webSocketService';

/**
 * Hook for real-time allocation status updates via WebSocket
 * Implements "signal-then-fetch" pattern: WebSocket events trigger API refetch
 * 
 * @param {string} bookingId - The booking ID to listen for allocation events
 * @param {Function} onRefetchAllocationStatus - Callback to refetch allocation status API
 * @param {boolean} isActive - Whether the hook should be active (e.g., modal is open)
 */
export const useAllocationWebSocket = (bookingId, onRefetchAllocationStatus, isActive = true) => {
  const handlersRef = useRef({});
  const lastRefetchTimeRef = useRef(0);
  const isMountedRef = useRef(true);
  const onRefetchRef = useRef(onRefetchAllocationStatus);
  onRefetchRef.current = onRefetchAllocationStatus;

  // Throttle refetch to prevent API spam (max once per 500ms). Stable callback so effect deps don't change every render.
  const throttledRefetch = useCallback(() => {
    const now = Date.now();
    if (now - lastRefetchTimeRef.current < 500) {
      return;
    }
    lastRefetchTimeRef.current = now;
    const fn = onRefetchRef.current;
    if (isMountedRef.current && fn && typeof fn === 'function') {
      fn();
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isActive || !bookingId) {
      return;
    }

    // Handler for booking_accepted event
    const handleBookingAccepted = (data) => {
      if (data.bookingId === bookingId || data.booking_id === bookingId) {
        throttledRefetch();
      }
    };

    // Handler for booking_declined event
    const handleBookingDeclined = (data) => {
      if (data.bookingId === bookingId || data.booking_id === bookingId) {
        throttledRefetch();
      }
    };

    // Handler for driver_response_timeout event
    const handleDriverTimeout = (data) => {
      if (data.bookingId === bookingId || data.booking_id === bookingId) {
        throttledRefetch();
      }
    };

    // Register event listeners
    webSocketService.on('booking_accepted', handleBookingAccepted);
    webSocketService.on('booking_declined', handleBookingDeclined);
    webSocketService.on('driver_response_timeout', handleDriverTimeout);

    // Store handlers for cleanup
    handlersRef.current = {
      booking_accepted: handleBookingAccepted,
      booking_declined: handleBookingDeclined,
      driver_response_timeout: handleDriverTimeout
    };

    return () => {
      if (handlersRef.current.booking_accepted) {
        webSocketService.off('booking_accepted', handlersRef.current.booking_accepted);
      }
      if (handlersRef.current.booking_declined) {
        webSocketService.off('booking_declined', handlersRef.current.booking_declined);
      }
      if (handlersRef.current.driver_response_timeout) {
        webSocketService.off('driver_response_timeout', handlersRef.current.driver_response_timeout);
      }
      handlersRef.current = {};
    };
  }, [bookingId, isActive, throttledRefetch]);

  return {
    isListening: isActive && !!bookingId
  };
};
